import {
  buildImportSession,
  type ImportIssueSeverity
} from '../../utils/importTable'
import {
  isSyncableProductionRow,
  type BiomassDataRow
} from '../../utils/productionUnitTable'
import type { FeedLog, MortalityLog } from '../../types/production'
import type { WaterDataRow } from '../../utils/waterTable'
import type { EntityMutationSyncState } from '../../utils/entityMutationSync'
import type { ProductionSummaryImportSession } from '../datatable/productionSummaryImport'
import type { FeedImportSession } from '../feed/feedImport'
import type { MortalityImportSession } from '../biomass/mortalityImport'
import type { ImportWorkspaceSessionSnapshot } from './importWorkspaceSession'
import type { WaterImportSession } from '../water/waterImport'

export type ImportWorkspaceSyncSummary = {
  queuedRowIds: string[]
  queuedRows: number
  localOnlyRows: number
  syncState: EntityMutationSyncState | null
}

type WaterImportSavePlan = {
  target: 'water'
  nextRows: WaterDataRow[]
  syncRowIds: string[]
  readyCount: number
  skippedCount: number
  sync: ImportWorkspaceSyncSummary
}

type ProductionImportSavePlan = {
  target: 'productionDatatable'
  nextRows: BiomassDataRow[]
  syncRowIds: string[]
  readyCount: number
  skippedCount: number
  sync: ImportWorkspaceSyncSummary
}

type FeedImportSavePlan = {
  target: 'feed'
  nextRows: FeedLog[]
  syncRowIds: string[]
  readyCount: number
  skippedCount: number
  sync: ImportWorkspaceSyncSummary
}

type MortalityImportSavePlan = {
  target: 'mortality'
  nextRows: MortalityLog[]
  syncRowIds: string[]
  readyCount: number
  skippedCount: number
  sync: ImportWorkspaceSyncSummary
}

export type ImportWorkspaceSavePlan = WaterImportSavePlan | ProductionImportSavePlan | FeedImportSavePlan | MortalityImportSavePlan

const resolveSyncState = (queuedRows: number, isOnline: boolean): EntityMutationSyncState | null => {
  if (queuedRows <= 0) return null
  return isOnline ? 'pending' : 'offline'
}

const hasIssueSeverity = (issues: Array<{ severity: ImportIssueSeverity }>, severity: ImportIssueSeverity): boolean => {
  return issues.some((issue) => issue.severity === severity)
}

const isWaterImportSession = (session: ImportWorkspaceSessionSnapshot): session is WaterImportSession => {
  return session.selectedTarget?.targetKey === 'water'
}

const isProductionImportSession = (session: ImportWorkspaceSessionSnapshot): session is ProductionSummaryImportSession => {
  return session.selectedTarget?.targetKey === 'productionDatatable'
}

const isFeedImportSession = (session: ImportWorkspaceSessionSnapshot): session is FeedImportSession => {
  return session.selectedTarget?.targetKey === 'feed'
}

const isMortalityImportSession = (session: ImportWorkspaceSessionSnapshot): session is MortalityImportSession => {
  return session.selectedTarget?.targetKey === 'mortality'
}

const filterRemainingResults = <TResult extends { issues: Array<{ severity: ImportIssueSeverity }> }>(results: TResult[]): TResult[] => {
  return results.filter((result) => hasIssueSeverity(result.issues, 'error'))
}

export const buildImportWorkspaceRemainingSession = (session: ImportWorkspaceSessionSnapshot): ImportWorkspaceSessionSnapshot | null => {
  if (isWaterImportSession(session)) {
    const remainingResults = filterRemainingResults(session.results)
    if (remainingResults.length === 0) return null

    return buildImportSession({
      sourceFile: session.sourceFile,
      table: session.table,
      selectedTarget: session.selectedTarget,
      assignedContext: session.assignedContext,
      mappedHeaders: session.mappedHeaders,
      results: remainingResults
    })
  }

  if (isProductionImportSession(session)) {
    const remainingResults = filterRemainingResults(session.results)
    if (remainingResults.length === 0) return null

    return buildImportSession({
      sourceFile: session.sourceFile,
      table: session.table,
      selectedTarget: session.selectedTarget,
      assignedContext: session.assignedContext,
      mappedHeaders: session.mappedHeaders,
      results: remainingResults
    })
  }

  if (isFeedImportSession(session)) {
    const remainingResults = filterRemainingResults(session.results)
    if (remainingResults.length === 0) return null

    return buildImportSession({
      sourceFile: session.sourceFile,
      table: session.table,
      selectedTarget: session.selectedTarget,
      assignedContext: session.assignedContext,
      mappedHeaders: session.mappedHeaders,
      results: remainingResults
    })
  }

  if (isMortalityImportSession(session)) {
    const remainingResults = filterRemainingResults(session.results)
    if (remainingResults.length === 0) return null

    return buildImportSession({
      sourceFile: session.sourceFile,
      table: session.table,
      selectedTarget: session.selectedTarget,
      assignedContext: session.assignedContext,
      mappedHeaders: session.mappedHeaders,
      results: remainingResults
    })
  }

  return null
}

export const buildImportWorkspaceSavePlan = (args: {
  session: ImportWorkspaceSessionSnapshot
  existingProductionRows: BiomassDataRow[]
  existingWaterRows?: WaterDataRow[]
  isOnline?: boolean
}): ImportWorkspaceSavePlan | null => {
  const online = args.isOnline ?? false

  if (isWaterImportSession(args.session)) {
    const readyRows = args.session.results
      .filter((result) => !hasIssueSeverity(result.issues, 'error'))
      .map((result) => result.record)
    const queuedRowIds = readyRows.map((row) => row.id)
    const queuedRows = readyRows.length
    // #38: preserve existing water snapshot. Drop any existing row whose id
    // collides with an imported row (imported wins, matching upsert intent).
    const readyIdSet = new Set(queuedRowIds)
    const preservedExistingRows = (args.existingWaterRows ?? []).filter((row) => !readyIdSet.has(row.id))

    return {
      target: 'water',
      nextRows: [...readyRows, ...preservedExistingRows],
      syncRowIds: readyRows.map((row) => row.id),
      readyCount: readyRows.length,
      skippedCount: args.session.results.length - readyRows.length,
      sync: {
        queuedRowIds,
        queuedRows,
        localOnlyRows: 0,
        syncState: resolveSyncState(queuedRows, online)
      }
    }
  }

  if (isProductionImportSession(args.session)) {
    const readyRows = args.session.results
      .filter((result) => !hasIssueSeverity(result.issues, 'error'))
      .map((result) => result.record)
    const syncableReadyRows = readyRows.filter(isSyncableProductionRow)
    const queuedRowIds = syncableReadyRows.map((row) => row.id)
    const queuedRows = syncableReadyRows.length

    return {
      target: 'productionDatatable',
      nextRows: [...readyRows, ...args.existingProductionRows],
      syncRowIds: readyRows.map((row) => row.id),
      readyCount: readyRows.length,
      skippedCount: args.session.results.length - readyRows.length,
      sync: {
        queuedRowIds,
        queuedRows,
        localOnlyRows: readyRows.length - queuedRows,
        syncState: resolveSyncState(queuedRows, online)
      }
    }
  }

  if (isFeedImportSession(args.session)) {
    const readyRows = args.session.results
      .filter((result) => !hasIssueSeverity(result.issues, 'error'))
      .map((result) => result.record)
    const queuedRowIds = readyRows.map((row) => row.id)
    const queuedRows = readyRows.length

    return {
      target: 'feed',
      nextRows: readyRows,
      syncRowIds: queuedRowIds,
      readyCount: readyRows.length,
      skippedCount: args.session.results.length - readyRows.length,
      sync: {
        queuedRowIds,
        queuedRows,
        localOnlyRows: 0,
        syncState: resolveSyncState(queuedRows, online)
      }
    }
  }

  if (isMortalityImportSession(args.session)) {
    const readyRows = args.session.results
      .filter((result) => !hasIssueSeverity(result.issues, 'error'))
      .map((result) => result.record)
    const queuedRowIds = readyRows.map((row) => row.id)
    const queuedRows = readyRows.length

    return {
      target: 'mortality',
      nextRows: readyRows,
      syncRowIds: queuedRowIds,
      readyCount: readyRows.length,
      skippedCount: args.session.results.length - readyRows.length,
      sync: {
        queuedRowIds,
        queuedRows,
        localOnlyRows: 0,
        syncState: resolveSyncState(queuedRows, online)
      }
    }
  }

  return null
}